import {
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { CATCHUP_BOOKKEEPING_PRICE_PER_MONTH } from '@/constants/pricingConstants'

export interface DueTodayLine {
  /** Stable across renders, and unique even when two lines share a label. */
  key: string
  /** What the charge is, on its own line with the amount. */
  label: string
  /** How it was worked out, set under the label. Null when there is nothing to
   *  qualify. */
  detail: string | null
  /** Signed: a credit is negative, so the lines sum to the total charged. */
  amountCents: number
}

export interface DueTodayInput {
  /** Display name of the tier being quoted; falls back to "Plan" when unknown. */
  planName: string | null
  /** The tier's price for the interval on screen, or null before offers load. */
  planPreviewCents: number | null
  isAnnual: boolean
  freeTrialEnabled: boolean
  /** Whether a catch-up bookkeeping fee applies at all (months owed, not waived). */
  cubkApplies: boolean
  /** Whether that fee is charged now rather than after the trial. */
  cubkChargedToday: boolean
  /** Waived outright, as opposed to merely deferred. Both leave it off today's
   *  charge, but only a deferred fee is still owed. */
  cubkWaived: boolean
  cubkMonthCount: number
  cubkOriginalCents: number
  cubkDiscountedCents: number
  percentOff: number | null | undefined
}

/** The summary quotes whole dollars unless there are real cents to show. */
export const money = (cents: number): string =>
  formatCurrencyFromCents(cents, { hideDecimalsIfZero: true })

/**
 * The itemization behind the due-today figure, in the order it reads.
 *
 * Every line is something charged or credited *today*, so the amounts always sum
 * to the total: a fee deferred past the trial is omitted rather than listed at
 * zero, and a free trial appears as a credit against the full plan price rather
 * than as a plan line quietly showing nothing.
 *
 * A waived fee is the one exception, listed at zero: it sums the same either
 * way, and it is the only evidence in the summary that the discount applied.
 */
export const dueTodayLines = (input: DueTodayInput): DueTodayLine[] => {
  const lines: DueTodayLine[] = []
  // "Plan" alone when the tier is not known yet, rather than "Plan Plan".
  const name = input.planName ? `${input.planName} Plan` : 'Plan'

  if (input.planPreviewCents !== null) {
    lines.push({
      key: 'plan',
      label: name,
      detail: input.isAnnual
        ? `12 months at ${money(Math.round(input.planPreviewCents / 12))}/mo`
        : 'Billed monthly',
      amountCents: input.planPreviewCents,
    })
    if (input.freeTrialEnabled) {
      lines.push({
        key: 'free-trial',
        label: 'Free trial',
        detail: 'Nothing charged today',
        amountCents: -input.planPreviewCents,
      })
    }
  }

  if (input.cubkApplies && (input.cubkChargedToday || input.cubkWaived)) {
    const months = `${input.cubkMonthCount} month${input.cubkMonthCount === 1 ? '' : 's'}`
    const rate = formatCurrency(CATCHUP_BOOKKEEPING_PRICE_PER_MONTH, {
      hideDecimalsIfZero: true,
    })
    lines.push({
      key: 'catch-up',
      label: 'Catch up bookkeeping',
      detail: `${months} at ${rate}/mo`,
      amountCents: input.cubkWaived ? 0 : input.cubkOriginalCents,
    })
    // A credit under a zero line would cancel a charge never made.
    if (!input.cubkWaived) {
      const discount = input.cubkOriginalCents - input.cubkDiscountedCents
      if (discount > 0) {
        lines.push({
          key: 'catch-up-discount',
          label: 'Catch up bookkeeping',
          detail: input.percentOff ? `${input.percentOff}% off` : 'Discount',
          amountCents: -discount,
        })
      }
    }
  }

  return lines
}

/**
 * What the customer is not being charged, for the line under the total. A
 * deferred fee counts for nothing here: it is postponed, not saved.
 */
export const dueTodaySavingsCents = (input: DueTodayInput): number => {
  if (!input.cubkApplies) return 0
  if (!input.cubkChargedToday && !input.cubkWaived) return 0
  return Math.max(0, input.cubkOriginalCents - input.cubkDiscountedCents)
}

/** Signed display amount, so a credit reads as "-$693" rather than as a charge. */
export const formatLineAmount = (amountCents: number): string =>
  amountCents < 0 ? `-${money(-amountCents)}` : money(amountCents)
